import React from 'react';
import { MarkdownWithHighlightPowers } from './MarkdownWithHighlightPowers';
import { css } from "@emotion/react";

// Function to extract just the first header or first few lines from markdown content
const extractHeaderFromMarkdown = (markdownContent: string): string => {
  // First check for heading markers
  const headingRegex = /^(#+)\s+(.+)$/m;
  const headingMatch = markdownContent.match(headingRegex);
  
  if (headingMatch) {
    return headingMatch[0];
  }
  
  // If no heading, check for bold text
  const boldRegex = /\*\*(.+?)\*\*/;
  const boldMatch = markdownContent.match(boldRegex);
  
  if (boldMatch) {
    return boldMatch[0];
  }
  
  // If no bold text or heading, return the first line or a truncated version
  const firstLine = markdownContent.split('\n')[0];
  if (firstLine && firstLine.length > 0) {
    return firstLine.length > 60 ? firstLine.substring(0, 60) + '...' : firstLine;
  }
  
  // Fallback
  return markdownContent.substring(0, 50) + '...';
};

// Extract first TimeReference / TimelineSectionBar directive if present
const extractTimeReference = (markdownContent: string): string | null => {
  const timeRefRegex = /:TimelineSectionBar\{[^}]+\}/;
  const match = markdownContent.match(timeRefRegex);
  return match ? match[0] : null;
};

const collapsedStyle = css`
  position: relative;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  // padding-right: 24px;
  height: 100%;
  display: flex;
  flex-direction: column;

  h5 {
    font-size: 1.1rem;
  }
`;

const expandedStyle = css`
  position: relative;
  width: 100%;
`;

const markdownContainerStyle = css`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

// When collapsed, we clip the full content (including embeds) to maintain mount state
const clippedWhenCollapsed = (isCollapsed: boolean) => css`
  position: relative;
  ${isCollapsed
    ? `
      height: 0;
      max-height: 0;
      overflow: clip;
      opacity: 0;
      pointer-events: none;
      visibility: hidden;
      contain: layout paint;
    `
    : `
      max-height: none;
      opacity: 1;
      pointer-events: auto;
    `}
`;

// Overlay for collapsed state showing header/time ref while content is clipped
const collapsedOverlayStyle = css`
  position: relative;
  z-index: 1;
`;

// Memoized version of this component to prevent re-renders
export const CollapsibleMarkdown = React.memo(
  (props: { 
    rawMarkdownContent: string;
    isCollapsed: boolean;
    shouldAnimate?: boolean;
    nodeIndex?: number;
    totalNodes?: number;
    topic?: string;
    topicColor?: string;
    visited?: boolean;
  }) => {
    const { rawMarkdownContent, isCollapsed, shouldAnimate = true, nodeIndex, totalNodes, topic, topicColor, visited } = props;
    
    const header = extractHeaderFromMarkdown(rawMarkdownContent);
    const timeRef = extractTimeReference(rawMarkdownContent);
    const overlayMarkdown = timeRef ? `${timeRef}\n${header}` : header;
    
    const transitionStyle = shouldAnimate ? css`
      /* Only animate paint properties to avoid layout thrash */
      transition: opacity 0.2s ease;
    ` : css``;

    return (
      <div css={[
        markdownContainerStyle, 
        isCollapsed ? collapsedStyle : expandedStyle,
        shouldAnimate && transitionStyle,
        css`
          position: relative;
          min-height: ${isCollapsed ? '80px' : 'auto'};
        `
      ]}>
        {/* Content section (always rendered) - clipped when collapsed to preserve embedded iframes */}
        <div css={css`
          flex: 1;
          padding: 8px 12px;
          background-color: ${isCollapsed ? 'rgba(0, 0, 0, 0.15)' : 'transparent'};
        `}>
          <div css={clippedWhenCollapsed(isCollapsed)}>
            <MarkdownWithHighlightPowers rawMarkdownContent={rawMarkdownContent} />
          </div>
          {isCollapsed && (
            <div css={collapsedOverlayStyle}>
              <MarkdownWithHighlightPowers rawMarkdownContent={overlayMarkdown} />
            </div>
          )}
        </div>
        
        {/* Footer section with topic name and node number */}
        {isCollapsed && nodeIndex !== undefined && (
          <div
            css={css`
              flex-shrink: 0;
              width: 100%;
              display: flex;
              justify-content: space-between;
              // align-items: flex-end;
              align-items: center;
              padding: 6px 8px;
              background: ${topicColor
                ? (topicColor.length === 7
                    ? (visited ? `${topicColor}1A` : `${topicColor}1A`) // visited uses previous default 1A (~10%), non-visited could use a different one in the future
                    : topicColor)
                : (visited ? 'rgba(52, 52, 52, 0.65)' : 'rgba(52, 52, 52, 0.55)')};
              backdrop-filter: blur(4px);
              border-bottom-left-radius: 7px;
              border-bottom-right-radius: 7px;
              user-select: none;
              pointer-events: none;
              min-height: 24px;
              font-size: 11px;
            `}
          >
            {/* Topic name with color indicator */}
            {topic && (
              <div css={css`
                display: flex;
                align-items: flex-start;
                color: rgba(255, 255, 255, 0.9);
                max-width: 85%;
                min-width: 0;
                overflow: hidden;
                padding-right: 8px;
              `}>
                {!visited && (
                  <span css={css`
                    flex-shrink: 0;
                    display: inline-block;
                    width: 9px;
                    height: 9px;
                    border-radius: 50%;
                    background: ${topicColor || '#888'};
                    margin-right: 6px;
                    margin-top: 3px;
                  `}></span>
                )}
                <span css={css`
                  display: inline-block;
                  word-break: break-word;
                  line-height: 1.3;
                  padding-bottom: 2px;
                `}>
                  {topic}
                </span>
              </div>
            )}
            
            {/* Node number badge */}
            <div css={css`
              font-size: 10px;
              line-height: 1;
              min-width: 20px;
              height: 20px;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              border: 1px solid rgba(255, 255, 255, 0.9);
              border-radius: 50%;
              background: rgba(30, 30, 30, 0.85);
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
              flex-shrink: 0;
            `}>
              {nodeIndex + 1}
            </div>
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if any of these props change
    return (
      prevProps.rawMarkdownContent === nextProps.rawMarkdownContent &&
      prevProps.isCollapsed === nextProps.isCollapsed &&
      prevProps.visited === nextProps.visited
    );
  }
);
